import { IFilterGrades } from "@src/entities/requestHH/api/interfaces/filtersResponseDto";
import { IStructuredVacancyDto } from "@src/entities/requestHH/api/interfaces/jobRequestDto";
import {
  CreatingSearchHHForm,
  TEducation,
  TEducationTypes,
  TExperience,
  TRegions,
  TSchedule,
  mapStringsToEditableList,
} from "@src/features/creatingSearchHH/ui/constans";

const EMPTY_VALUES = ["", "Нет данных.", "нет данных", "Нет данных"];
export const ALL_REGIONS_ID = "113";

export const emptyGrades: IFilterGrades = {
  systems: [],
  levels: [],
};

const isMeaningfulString = (value?: string | null) => {
  if (!value) return false;

  return !EMPTY_VALUES.includes(value.trim());
};

const cleanStringArray = (value?: string[]) => {
  return (value ?? [])
    .map((item) => item?.trim())
    .filter((item): item is string => Boolean(item))
    .filter((item) => isMeaningfulString(item));
};

const numberToString = (value?: number | null) => {
  return value !== undefined && value !== null && value !== 0
    ? String(value)
    : "";
};

const filterByHhIds = <T extends { hh_id?: string | number }>(
  options: T[],
  ids?: string[],
): T[] => {
  if (!ids?.length) return [];

  return options.filter((item) => ids.includes(String(item.hh_id)));
};

const filterRegionsByIds = (
  options: TRegions[],
  ids?: string[],
): TRegions[] => {
  if (!ids?.length) return [];

  return options.filter((item) => ids.includes(String(item.id)));
};

const EXPERIENCE_RANGES_BY_HH_ID: Record<
  string,
  { minMonths: number; maxMonths: number | null }
> = {
  noExperience: {
    minMonths: 0,
    maxMonths: 0,
  },
  between1And3: {
    minMonths: 1,
    maxMonths: 35,
  },
  between3And6: {
    minMonths: 36,
    maxMonths: 71,
  },
  moreThan6: {
    minMonths: 72,
    maxMonths: null,
  },
};

const isRangesIntersect = (
  first: { minMonths: number; maxMonths: number | null },
  second: { minMonths: number; maxMonths: number | null },
) => {
  const firstMax = first.maxMonths ?? Number.POSITIVE_INFINITY;
  const secondMax = second.maxMonths ?? Number.POSITIVE_INFINITY;

  return first.minMonths <= secondMax && second.minMonths <= firstMax;
};

const mapExperienceMonthsToOptions = <T extends { hh_id: string }>(
  experienceOptions: T[],
  workExperienceFrom?: number | null,
  workExperienceTo?: number | null,
): T[] => {
  if (workExperienceFrom == null && workExperienceTo == null) {
    return [];
  }

  const from = workExperienceFrom ?? 0;
  const to = workExperienceTo ?? null;

  const candidateRange = {
    minMonths: from,
    maxMonths: to,
  };

  return experienceOptions.filter((option) => {
    const optionRange = EXPERIENCE_RANGES_BY_HH_ID[option.hh_id];

    if (!optionRange) return false;

    return isRangesIntersect(candidateRange, optionRange);
  });
};

const findEducationOption = <
  T extends { id?: string | number; hh_id?: string | number; name?: string },
>(
  educationOptions: T[],
  educationId?: string | number | null,
  educationType?: string | null,
): T | null => {
  if (educationId) {
    const byId =
      educationOptions.find(
        (item) => String(item.id) === String(educationId),
      ) ??
      educationOptions.find(
        (item) => String(item.hh_id) === String(educationId),
      );

    if (byId) {
      return byId;
    }
  }

  if (educationType) {
    const normalizedEducationType = educationType.trim().toLowerCase();

    return (
      educationOptions.find(
        (item) => item.name?.trim().toLowerCase() === normalizedEducationType,
      ) ?? null
    );
  }

  return null;
};

type TMapStructuredVacancyDtoToFormParams = {
  baseValues: CreatingSearchHHForm;
  structuredVacancy: IStructuredVacancyDto;
  areaOptions: TRegions[];
  experienceOptions: TExperience[];
  educationOptions: TEducationTypes[];
  workFormatOptions: TSchedule[];
  jobStatusOptions: TEducation[];
  resumeRelocationOptions: TEducation[];
  gradeOptions: IFilterGrades;
};

export const mapStructuredVacancyDtoToForm = ({
  baseValues,
  structuredVacancy,
  areaOptions,
  experienceOptions,
  educationOptions,
  workFormatOptions,
  jobStatusOptions,
  resumeRelocationOptions,
  gradeOptions,
}: TMapStructuredVacancyDtoToFormParams): CreatingSearchHHForm => {
  const mandatorySkills = cleanStringArray(
    structuredVacancy.requirements?.mandatory,
  );
  const prioritySkills = cleanStringArray(
    structuredVacancy.requirements?.priority,
  );
  const optionalSkills = cleanStringArray(
    structuredVacancy.requirements?.optional,
  );

  const area = filterRegionsByIds(areaOptions, structuredVacancy.location_ids);

  const education = findEducationOption(
    educationOptions,
    structuredVacancy.education_id,
    structuredVacancy.education_type,
  );

  const jobSearchStatus = filterByHhIds(
    jobStatusOptions,
    structuredVacancy.job_search_status_ids,
  );

  const relocation = structuredVacancy.relocation_types_ids?.length
    ? resumeRelocationOptions.find(({ hh_id }) =>
        structuredVacancy.relocation_types_ids?.includes(hh_id),
      ) || null
    : null;

  const workFormat = structuredVacancy.work_format_ids?.length
    ? workFormatOptions.reduce((acc: Array<TSchedule>, el) => {
        if (structuredVacancy.work_format_ids?.includes(el.id)) {
          acc.push(el);
        }
        return acc;
      }, [])
    : [];

  const mappedExperience = mapExperienceMonthsToOptions(
    experienceOptions,
    structuredVacancy.work_experience_from,
    structuredVacancy.work_experience_to,
  );

  const profile = structuredVacancy.grade_ids?.system_id
    ? gradeOptions.systems.find(
        ({ id }) => id === structuredVacancy.grade_ids?.system_id,
      )
    : null;

  const specialistLevel =
    structuredVacancy.grade_ids?.system_id &&
    structuredVacancy?.grade_ids?.level_ids?.length
      ? gradeOptions.levels
          .find(
            ({ system_id }) =>
              system_id === structuredVacancy.grade_ids?.system_id,
          )
          ?.levels.filter(({ id }) =>
            structuredVacancy?.grade_ids?.level_ids?.includes(id),
          )
      : [];

  const driverLicenseCategories = structuredVacancy.car_license?.length
    ? structuredVacancy.car_license
    : baseValues.driverLicenseCategories;

  return {
    ...baseValues,
    experience: mappedExperience.length
      ? mappedExperience
      : baseValues.experience,
    companyIndustry: isMeaningfulString(structuredVacancy.company_industry)
      ? structuredVacancy.company_industry!
      : baseValues.companyIndustry,
    priority: prioritySkills.length
      ? mapStringsToEditableList(prioritySkills)
      : baseValues.priority,
    optional: optionalSkills.length
      ? mapStringsToEditableList(optionalSkills)
      : baseValues.optional,
    mandatory: mandatorySkills.length
      ? mapStringsToEditableList(mandatorySkills)
      : baseValues.mandatory,
    exclusionCriteria: structuredVacancy.exclusion_criteria?.length
      ? mapStringsToEditableList(structuredVacancy.exclusion_criteria)
      : baseValues.exclusionCriteria,
    isRussia: structuredVacancy.location_ids?.includes(ALL_REGIONS_ID)
      ? true
      : false,
    workFormat: workFormat ?? baseValues.workFormat,
    resumeRelocation: relocation ?? baseValues.resumeRelocation,
    salary_from:
      numberToString(structuredVacancy.salary_from) || baseValues.salary_from,
    salary_to:
      numberToString(structuredVacancy.salary_to) || baseValues.salary_to,
    showWithout:
      structuredVacancy.match_without_salary ?? baseValues.showWithout,
    ageFrom: numberToString(structuredVacancy.age_from) || baseValues.ageFrom,
    ageTo: numberToString(structuredVacancy.age_to) || baseValues.ageTo,
    isWithoutAge:
      structuredVacancy.match_without_age ?? baseValues.isWithoutAge,
    isFrequentJobChanges:
      structuredVacancy.exclude_job_hoppers ?? baseValues.isFrequentJobChanges,
    isHasCar: structuredVacancy?.requires_car,
    citizenship: structuredVacancy.rf_citizenship ?? baseValues.citizenship,
    gender: structuredVacancy.gender_id ?? baseValues.gender,
    profile: profile ?? baseValues.profile,
    specialistLevel: specialistLevel ?? baseValues.specialistLevel,
    experienceFrom: structuredVacancy.work_experience_from
      ? String(structuredVacancy.work_experience_from)
      : "",
    experienceTo: structuredVacancy.work_experience_to
      ? String(structuredVacancy.work_experience_to)
      : "",
    vacancyName: structuredVacancy?.vacancy_name || "",
    education,
    jobSearchStatus,
    area,
    driverLicenseCategories,
    isEduOptional: structuredVacancy?.is_edu_optional ?? false,
  };
};
